import { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { AdminProductImagesSection } from '@/components/admin/AdminProductImagesSection';
import { ProductImage } from '@/components/admin/ProductImageUpload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useProducerProducts } from '@/hooks/useProducerProducts';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

export default function AdminProductEdit() {
  const { producerId, productId } = useParams<{ producerId: string; productId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { products, loading, updateProduct } = useProducerProducts(producerId);
  const { toast } = useToast();
  
  const imagesSectionRef = useRef<HTMLDivElement>(null);
  const [highlightImages, setHighlightImages] = useState(false);

  // Find the product
  const product = products.find(p => p.id === productId);

  // Form states
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priceKr, setPriceKr] = useState('');
  const [productImages, setProductImages] = useState<ProductImage[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Initialize form when product loads
  useEffect(() => {
    if (product && !initialized) {
      setTitle(product.title);
      setDescription(product.description || '');
      setPriceKr((product.price_ore / 100).toString());
      // Product images are managed via media_assets — no legacy images parsing needed
      setProductImages([]);
      setInitialized(true);
    }
  }, [product, initialized]);

  // Handle focus=images param (scroll and highlight)
  useEffect(() => {
    if (initialized && searchParams.get('focus') === 'images') {
      // Scroll to images section
      setTimeout(() => {
        imagesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightImages(true);
        
        // Remove highlight after 2 seconds
        setTimeout(() => {
          setHighlightImages(false);
        }, 2000);
        
        // Remove focus param from URL
        searchParams.delete('focus');
        setSearchParams(searchParams, { replace: true });
      }, 100);
    }
  }, [initialized, searchParams, setSearchParams]);

  const handleSave = async (publish = false) => {
    if (!title.trim() || !productId) {
      toast({
        title: 'Titel krävs',
        description: 'Ange en titel för produkten.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);

    try {
      const priceOre = Math.round(parseFloat(priceKr || '0') * 100);
      const status = publish ? 'public' : (product?.status || 'draft');

      await updateProduct({
        id: productId,
        title: title.trim(),
        description: description.trim() || undefined,
        price_ore: priceOre,
        status,
      });

      toast({
        title: 'Ändringar sparade',
        description: `${title} har uppdaterats.`,
      });

      navigate(`/admin/producers/${producerId}/products`);
    } catch (err) {
      toast({
        title: 'Kunde inte spara',
        description: 'Något gick fel. Försök igen.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/admin/producers/${producerId}/products`);
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'public':
        return 'Publicerad';
      default:
        return 'Utkast';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  if (!product) {
    return (
      <AdminLayout>
        <div className="max-w-2xl mx-auto text-center py-16">
          <h1 className="text-xl font-medium text-foreground mb-2">
            Produkten hittades inte
          </h1>
          <p className="text-muted-foreground mb-6">
            Produkten du söker finns inte längre.
          </p>
          <Button asChild>
            <Link to={`/admin/producers/${producerId}/products`}>
              Tillbaka till produkter
            </Link>
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="w-full max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2">
            <Link to={`/admin/producers/${producerId}/products`}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tillbaka till produkter
            </Link>
          </Button>

          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-foreground">Redigera produkt</h1>
            <Badge variant="secondary">
              {getStatusLabel(product.status)}
            </Badge>
          </div>
          <p className="text-muted-foreground mt-1">
            Uppdatera produktinformationen nedan.
          </p>
        </div>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>Produktdetaljer</CardTitle>
            <CardDescription>
              Grundläggande information om produkten.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="product-title">Titel *</Label>
              <Input
                id="product-title"
                placeholder="T.ex. Nötkött från gården"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="product-price">Pris (kr)</Label>
              <Input
                id="product-price"
                type="number"
                min="0"
                step="1"
                placeholder="0"
                value={priceKr}
                onChange={(e) => setPriceKr(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="product-description">Beskrivning</Label>
              <Textarea
                id="product-description"
                placeholder="Beskriv produkten..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
              />
            </div>
          </CardContent>
        </Card>

        {/* Product Images */}
        {producerId && productId && (
          <div
            ref={imagesSectionRef}
            className={cn(
              'transition-all duration-500 rounded-lg',
              highlightImages && 'ring-2 ring-primary ring-offset-2 ring-offset-background'
            )}
          >
            <AdminProductImagesSection
              producerId={producerId}
              productId={productId}
              images={productImages}
              onImagesChange={setProductImages}
              disabled={isSaving}
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Avbryt
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleSave(false)}
            disabled={isSaving || !title.trim()}
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Spara ändringar
          </Button>
          <Button
            onClick={() => handleSave(true)}
            disabled={isSaving || !title.trim()}
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Publicera
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
